import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, List, Stack, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { GetRulesByStatusQuery } from '../../../../graphql/generated';
import { RuleCard } from '../../molecules';
import { useRuleConditionLookups } from '../../utils';

type RetentionRule = NonNullable<
  GetRulesByStatusQuery['getRulesByStatus']
>[number];

interface WaitingForApprovalProps {
  rules: RetentionRule[];
  isLoading: boolean;
}

export const WaitingForApproval = ({
  rules,
  isLoading,
}: WaitingForApprovalProps) => {
  const [expandedRules, setExpandedRules] = useState<Record<string, boolean>>(
    {},
  );
  const { tokens } = useTheme<LensTheme>();

  const { relationshipTypes, caseCategories } = useRuleConditionLookups(
    rules.map((rule) => rule.conditions),
  );

  const toggleRule = (id: string) => {
    setExpandedRules((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.core.spacing[3].value,
        }}
      >
        Loading...
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.core.spacing[3].value,
      }}
    >
      <Stack
        direction="column"
        sx={{
          gap: tokens.core.spacing['0_5'].value,
          padding: tokens.core.spacing[0].value,
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontWeight: tokens.semantic.fontWeight.emphasis.value }}
        >
          Waiting for Approval
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: tokens.semantic.color.type.muted.value }}
        >
          Rules requiring review and approval before going live
        </Typography>
      </Stack>
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.core.spacing[2].value,
          margin: 0,
          padding: 0,
          listStyle: 'none',
        }}
      >
        {rules.map((rule) => (
          <RuleCard
            key={rule.id}
            rule={rule}
            relationshipTypes={relationshipTypes}
            caseCategories={caseCategories}
            isExpanded={expandedRules[rule.id]}
            onToggle={toggleRule}
          />
        ))}
      </List>
    </Box>
  );
};
